import { getPeriodsForDashboard, type Period, useMe } from '@salescore/client-common'
import dayjs from 'dayjs'
import { Array, Option, pipe } from 'effect'
import { type Dispatch, useCallback, useMemo } from 'react'
import { useRecoilState } from 'recoil'

import { kpiParameterAtom } from '../recoil/navigation/atoms'

export const usePeriod = (): [Period | undefined, Dispatch<Period | undefined>] => {
  const { organization } = useMe()
  const periods = useMemo(() => getPeriodsForDashboard(organization.setting), [organization.setting])
  const [kpiParameter, setKpiParameter] = useRecoilState(kpiParameterAtom)

  const setPeriod = useCallback(
    (period: Period | undefined): void => {
      const picked = periods.find((x) => x.name === period?.name)

      setKpiParameter((oldValue) => ({
        ...oldValue,
        queryParameter: {
          ...oldValue.queryParameter,
          periodName: period?.name,
          // カスタムのときだけ、クエリパラメータとして保存する（それ以外の時は相対日付のはずであり、絶対日付して保存したくないので保存しない）
          startAt: picked?.startAt === undefined ? period?.startAt?.format(`YYYY-MM-DD HH:mm:ss`) : undefined,
          endAt: picked?.endAt === undefined ? period?.endAt?.format(`YYYY-MM-DD HH:mm:ss`) : undefined,
        },
      }))
    },
    [periods, setKpiParameter],
  )

  const period = useMemo(
    () =>
      pipe(
        periods,
        Array.findFirst((x) => x.name === kpiParameter.queryParameter?.periodName),
        Option.map((p) => ({
          ...p,
          // 日付でカスタムを指定した場合はPeriodにstartAt/endAtが存在しないため
          // parameter.queryParameter から値を取得する
          startAt:
            p.startAt ??
            (kpiParameter.queryParameter?.startAt !== undefined && kpiParameter.queryParameter.startAt !== ''
              ? dayjs(kpiParameter.queryParameter.startAt)
              : undefined),
          endAt:
            p.endAt ??
            (kpiParameter.queryParameter?.endAt !== undefined && kpiParameter.queryParameter.endAt !== ''
              ? dayjs(kpiParameter.queryParameter.endAt)
              : undefined),
        })),
        Option.getOrUndefined,
      ),
    [kpiParameter.queryParameter, periods],
  )

  return [period, setPeriod]
}
